import { LatLng } from '../utils/types';

type Bounds = { sw: [number, number]; ne: [number, number] };
type Component = { long_name: string; types: string[] };

export type PreferredLocation = {
  kind:
    | 'city'
    | 'neighborhood'
    | 'postal_code'
    | 'street'
    | 'address'
    | 'country'
    | 'region';
  name: string;
  city?: string;
  country?: string;
  street?: string;
  houseNumber?: string;
  center: LatLng;
  bounds: Bounds | null;
};

/** Keep the selected place's identity separate from the moving viewport label. */
export function preferredLocationFromResult(
  result: { types: string[]; address_components: Component[] },
  center: LatLng,
  bounds: Bounds | null,
): PreferredLocation | null {
  const components = result.address_components;
  const component = (...types: string[]) =>
    components.find((c) => types.some((type) => c.types.includes(type)))
      ?.long_name;
  const city = component(
    'locality',
    'postal_town',
    'administrative_area_level_3',
  );
  const country = component('country');
  const street = component('route');
  const houseNumber = component('street_number');
  const types = result.types || [];
  let kind: PreferredLocation['kind'];
  let name: string | undefined;

  if (street && houseNumber) {
    kind = 'address';
    name = street;
  } else if (types.includes('route')) {
    kind = 'street';
    name = street;
  } else if (
    types.some(
      (t) =>
        ['neighborhood', 'colloquial_area'].includes(t) ||
        t.startsWith('sublocality'),
    )
  ) {
    kind = 'neighborhood';
    name = components.find((c) =>
      c.types.some(
        (t) =>
          ['neighborhood', 'colloquial_area'].includes(t) ||
          t.startsWith('sublocality'),
      ),
    )?.long_name;
  } else if (types.includes('postal_code')) {
    kind = 'postal_code';
    name = component('postal_code');
  } else if (
    types.some((t) =>
      ['locality', 'postal_town', 'administrative_area_level_3'].includes(t),
    )
  ) {
    kind = 'city';
    name = city;
  } else if (types.includes('country')) {
    kind = 'country';
    name = country;
  } else if (types.some((t) => t.startsWith('administrative_area'))) {
    kind = 'region';
    name = components.find((c) =>
      c.types.some(
        (t) => types.includes(t) && t.startsWith('administrative_area'),
      ),
    )?.long_name;
  } else {
    return null;
  }
  return name
    ? { kind, name, city, country, street, houseNumber, center, bounds }
    : null;
}

/** Small pans preserve the search; browsing away permanently releases it. */
export function preferredLocationInView(
  place: PreferredLocation | null,
  viewport: Bounds,
): PreferredLocation | null {
  if (!place) return null;
  const bounds = place.bounds ?? {
    sw: [place.center.lng, place.center.lat],
    ne: [place.center.lng, place.center.lat],
  };
  return bounds.sw[0] <= viewport.ne[0] &&
    bounds.ne[0] >= viewport.sw[0] &&
    bounds.sw[1] <= viewport.ne[1] &&
    bounds.ne[1] >= viewport.sw[1]
    ? place
    : null;
}
